import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled, {keyframes } from 'styled-components';
import logo from '../../assets/at-details/at-logo_blk.png';

export const Footer = styled.footer`
  background-color: var(--maindark);
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  width: 100%;
  padding: 2rem 0rem;

  @media (min-width: 768px) {
    padding: 4rem 2rem 2rem 1rem;
  }

  @media (min-width: 1024px) {
    padding: 4rem 2rem 2rem 2rem;
  }
`;

interface FootRowProps {
  $justify?: "left" | "right" | "space-between";
}

export const FootRow = styled.div<FootRowProps>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: ${({ $justify }) =>
    $justify === "left"
      ? "flex-start"
      : $justify === "right"
        ? "flex-end"
        : "space-between"};
  width: 100%;
`;

export const FootListContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  margin: 0 20px;
`;

export const FootLogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 20px;
`;

export const NameLink = styled(Link)<{ to: string }>`
  color: var(--mainlight);
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  text-transform: uppercase;
  margin: 5px 0;

  &:hover,
  &:focus {
    text-decoration: none;
  }

  /* Tablet */
  @media (min-width: 768px) {
    font-size: 18px;
  }
`;

export const FootLinks = styled(Link)<{ to: string }>`
  position: relative;
  color: var(--mainlight);
  font-size: 13px;
  text-transform: uppercase;
  text-decoration: none;
  display: inline-block;
  margin: 5px 0;

  &:hover {
    color: var(--darktext);
  }

  &::before {
    content: "";
    position: absolute;
    display: block;
    width: 0;
    height: 4px;
    bottom: -2px;
    left: 0;
    background-color: var(--bg-pink);
    transform-origin: left;
    transition: width 0.5s ease;
  }

  &:hover::before {
    width: 100%;
  }

  /* Tablet */
  @media (min-width: 768px) {
    font-size: 16px;
  }
`;

export const FootP = styled.p`
  color: var(--mainlight);
  font-size: 12px;
  font-weight: 300;
  margin: 4px auto;
`;

export const FootContactContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 4px 0;
`;

interface HighlightSpanProps {
  $color: string;
}

export const HighlightSpanFoot = styled.span<HighlightSpanProps>`
  color: ${(props) => props.$color};
`;

export const FootContactH1 = styled.h1`
  color: var(--mainlight);
  margin-bottom: 20px;
  font-weight: 600;
  font-size: 24px;
  line-height: 1.6;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  width: 280px;
`;

export const FootIcon = styled.img`
  height: 22px;
  width: 22px;
  margin: 5px 10px 0 0;
`;

export const FootContactInfo = styled.a`
  color: var(--mainlight);
  margin: 8px 0;
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 1px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
    text-decoration-thickness: 2px;
    text-decoration-color: var(--bg-pink);
    text-underline-offset: 5px;
  }
`;

export const FootContactInfoAdress = styled.address`
  color: var(--mainlight);
  margin: 8px 0;
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 1px;
  font-style: normal;
`;

export const FootContactInfoParagraph = styled.p`
  color: var(--mainlight);
  margin: 8px 0;
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 1px;
`;

// New contact button
interface ContactButtonProps {
  to: string;
}

const KontaktWave = keyframes`
  0%, 100% { 
    transform: translateY(0); 
  }
  50% { 
    transform: translateY(-8px); 
  }
`;

const Letter = styled.span`
  display: inline-block;
`;

const ContactButton = styled.button`
  border-radius: 50px;
  border: 1px solid var(--mainlight);
  color: var(--mainlight);
  background-color: transparent;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
  text-decoration: none;
  cursor: pointer;
  padding: 10px 16px;
  margin-top: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;

  &:hover {
    transform: scale(1.02);
    transition: transform 0.2s ease-in-out;
    border: 1px solid var(--bg-pink);
    color: var(--bg-pink);
  }

  &:active {
    transform: scale(0.9);
    border: 1px solid var(--accent-pink);
    color: var(--accent-pink);
  }

  &:hover ${Letter} {
    animation: ${KontaktWave} 0.3s ease-in-out forwards;
  }
`;

export const ContactUsButton: React.FC<ContactButtonProps> = ({ to = "/" }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault();

    if (!to) return;

    const [path, hash] = to.includes("#") ? to.split("#") : [to, ""];
    const currentPath = location.pathname;
    const targetSection = hash ? document.getElementById(hash) : null;

    if (hash && currentPath === path) {
      targetSection?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(`${path}${hash ? `#${hash}` : ""}`);
    }
  };

  return (
    <ContactButton onClick={handleClick}>
      {("Kontakt" as string).split("").map((letter, i) => (
        <Letter key={i} style={{ animationDelay: `${i * 0.05}s` }}>
          {letter}
        </Letter>
      ))}
    </ContactButton>
  );
};

// andtech logo
const FooterLogo = styled.img`
  align-self: flex-end;
  width: 80px;
  margin: 0;

  /* Tablet */
  @media (min-width: 768px) {
    width: 100px;
    margin: -20px 0 0 0;
  }

  /* Desktop */
  @media (min-width: 1024px) {
    width: 150px;
    margin: -70px 0 0 0;
  }
`;

export const FooterLogoMini = () => {
  return (
    <Link to="/" aria-label="Navigate to the homepage">
      <FooterLogo src={logo} alt="AndTech logo" />
    </Link>
  );
};
